/**
 * useTitle Composable
 *
 * Manages the document title with an optional suffix (e.g. "Page | App Name").
 *
 * Features:
 * - Dynamic title updates via reactive ref or `setTitle()`
 * - Automatic suffix appending (defaults to VUE_APP_TITLE)
 * - Disable suffix per call with `{ suffix: false }`
 *
 * @param initialTitle - The initial page title
 * @param options - Optional configuration
 * @param options.suffix - Custom suffix string, `false` to disable, defaults to env app title
 *
 * @example
 * // With default suffix: "Dashboard | Diasporiques ABA Solutions LLC"
 * useTitle('Dashboard')
 *
 * // With custom suffix: "Dashboard | My App"
 * useTitle('Dashboard', { suffix: 'My App' })
 *
 * // Without suffix: "Dashboard"
 * useTitle('Dashboard', { suffix: false })
 */
import { ref, watch } from 'vue'

interface TitleOptions {
  /** Custom suffix appended after " | ". Set to `false` to disable. */
  suffix?: string | false
}

export function useTitle(initialTitle: string, options?: TitleOptions) {
  const title = ref(initialTitle)

  // Resolve suffix: explicit string > env var > disabled
  const suffix: string | null = (() => {
    if (options?.suffix === false) return null
    if (typeof options?.suffix === 'string') return options.suffix
    const appTitle = process.env.VUE_APP_TITLE
    return appTitle || null
  })()

  const formatTitle = (pageTitle: string): string => {
    return suffix ? `${pageTitle} | ${suffix}` : pageTitle
  }

  watch(title, (newTitle) => {
    document.title = formatTitle(newTitle)
  }, { immediate: true })

  return {
    title,
    setTitle: (newTitle: string) => {
      title.value = newTitle
    }
  }
} 